import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { selectColor, Icon, sendTrackingInfo } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import { PostingsOperations } from '@panel/avisos/ducks/posting';
import { CloseFilterGroupButton } from './CloseFilterGroupButton';
import { staticFilters } from '../../../../constants/filtersOptions';
import SearchBox from './SearchBox';

const Wrapper = styled.div`
  margin: 0;
  flex-direction: column;
  overflow: scroll;
  height: 100%;
`;

const ContentMargin = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px 24px;
`;

const CloseModalButton = styled.div`
  z-index: 1;
  position: fixed;
  width: 97%;
  height: 48px;
  display: flex;
  background-color: rgba(255, 255, 255, 0.9);
  align-items: center;
  padding-left: 20px;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 2.4rem;
  color: ${selectColor('gray50')};
  margin-top: 40px;
  margin-bottom: 8px;
  font-weight: 600;
  height: 40px;
`;

const BulletsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Bullet = styled.div`
  border-radius: 12px;
  border: 1px solid ${selectColor('gray25')};
  display: flex;
  padding: 4px 8px;
  background-color: ${selectColor('gray5')};
  margin: 0 8px 8px 0;
  height: 26px;
`;

const BulletText = styled.div`
  height: 16px;
  color: ${selectColor('gray50')};
  font-size: 1.2rem;
  padding: 0;
  margin: 0;
`;

const BulletCross = styled.div`
  height: 16px;
  width: 16px;
  margin-left: 8px;
  display: flex;
  align-content: center;
  justify-content: center;
`;

const FiltersGroupsWrapper = styled.div`
  width: 100%;
  flex-grow: 0;
  margin-bottom: 48px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
  border-bottom: 1px solid ${selectColor('gray25')};
`;

const FilterTitleBar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FilterTitle = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  color: ${selectColor('gray100')};
  font-weight: bold;
  pointer-events: none;
`;

const FilterNameGroup = styled.div`
  max-height: 0;
  transition: max-height 0.3s ease-out;
  overflow: hidden;
  &.selected {
    max-height: 920px;
    transition: max-height 0.6s ease-in-out;
  }
`;

const FilterRow = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterName = styled.div`
  display: flex;
  color: ${selectColor('gray50')};
  &.selected {
    color: ${selectColor('gray100')};
  }
  &.selected + div {
    display: flex;
  }
  &.disabled {
    color: ${selectColor('gray25')};
  }
  &.disabled + div {
    display: none;
  }
`;

const FilterCheck = styled.div`
  display: none;
`;

const ApplyFiltersButtonWrapper = styled.div`
  position: fixed;
  bottom: 0px;
  width: 100%;
  padding: 16px 24px;
  background-color: ${selectColor('white')};
`;

const ApplyFiltersButton = styled.button`
  height: 40px;
  border-radius: 4px;
  background-color: ${selectColor('primary')};
  font-weight: bold;
  color: #fff;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  &:disabled {
    background-color: ${selectColor('gray25')};
  }
  &:active {
    background-color: ${selectColor('gray100')};
  }
`;

function FiltersModal(props) {
  const {
    selectedFilters,
    appliedFilters,
    filtersOrder,
    onFiltersModalToggle = () => {},
    onSelectedFilterEdit = () => {},
    onUpdateAppliedFilters = () => {}
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchBoxInputIsFocused, setSearchBoxInputIsFocused] = useState(false);
  const onSearchBoxInputFocus = () => setSearchBoxInputIsFocused(true);
  const onSearchBoxInputBlur = () => setSearchBoxInputIsFocused(false);

  const sortFilters = filters => {
    const result = {};
    for (let i = 0; i < filtersOrder.length; i += 1) {
      result[filtersOrder[i]] = filters[filtersOrder[i]];
    }
    return result;
  };

  const handleApplyFilters = () => {
    if (JSON.stringify(selectedFilters) !== JSON.stringify(appliedFilters)) {
      dispatch(PostingsOperations.reset());
      onUpdateAppliedFilters(selectedFilters);
      onFiltersModalToggle();
      Object.entries(selectedFilters).forEach(filter => {
        if (filter[1]) {
          const filterData = `Filtro-${t(`panel.filters.${filter[0]}.${filter[1]}`)}`;
          sendTrackingInfo('Mis avisos', filterData);
        }
      });
    }
  };

  const handleBackButon = () => {
    onSelectedFilterEdit(appliedFilters);
    onFiltersModalToggle();
  };

  const handleSelectedFilter = (name, value) => {
    onSelectedFilterEdit({ [name]: value });
  };

  const handleClick = e => {
    if (e.target.nextSibling.classList.contains('selected')) {
      e.target.nextSibling.classList.remove('selected');
      e.target.children[1].classList.remove('selected');
    } else {
      e.target.nextSibling.classList.add('selected');
      e.target.children[1].classList.add('selected');
    }
  };
  return (
    <Wrapper data-panel='filters'>
      <CloseModalButton data-panel='filters_closeModal' type='button' onClick={handleBackButon}>
        <Icon name='arrowLeft' width={24} height={24} colorFill='enabled' />
      </CloseModalButton>
      <ContentMargin>
        <ModalTitle>{t('panel.filters.title')}</ModalTitle>
        <BulletsWrapper>
          {Object.entries(sortFilters(selectedFilters)).map(
            bullet =>
              bullet[1] && (
                <Bullet
                  key={bullet[0]}
                  className={bullet[1]}
                  selectedFilters={bullet[1]}
                  onClick={() => handleSelectedFilter(bullet[0], '')}
                  data-panel={`filtersBulletSelected_${bullet[0]}_${bullet[1]}`}
                >
                  <BulletText>
                    {bullet[0] === 'searchCode' ? bullet[1] : t(`panel.filters.${bullet[0]}.${bullet[1]}`)}
                  </BulletText>

                  <BulletCross>
                    <Icon name='cross' width={16} height={16} viewBox='0 0 16 16' />
                  </BulletCross>
                </Bullet>
              )
          )}
        </BulletsWrapper>
        <SearchBox
          handleSelectedFilter={handleSelectedFilter}
          onSearchBoxInputFocus={onSearchBoxInputFocus}
          onSearchBoxInputBlur={onSearchBoxInputBlur}
        />
        <FiltersGroupsWrapper>
          {staticFilters.map(filterGroup => (
            <FilterGroup key={filterGroup.title}>
              <FilterTitleBar data-panel={`filtersSelectionTitle_${filterGroup.title}`} onClick={handleClick}>
                <FilterTitle key={filterGroup.title}>{t(`panel.filters.${filterGroup.title}`)}</FilterTitle>
                <CloseFilterGroupButton groupTitle={filterGroup.title} />
              </FilterTitleBar>
              <FilterNameGroup className={filterGroup.title === 'status' ? 'selected' : ''}>
                {filterGroup.filters.map(filter => (
                  <FilterRow
                    key={filter}
                    onClick={() =>
                      selectedFilters[filterGroup.title] !== filter
                        ? handleSelectedFilter(filterGroup.title, filter)
                        : handleSelectedFilter(filterGroup.title, '')
                    }
                    data-panel={`filtersSelectionValue_${filterGroup.title}_${filter}`}
                  >
                    <FilterName
                      className={
                        selectedFilters[filterGroup.title] !== '' &&
                        (selectedFilters[filterGroup.title] === filter ? 'selected' : 'disabled')
                      }
                    >
                      {t(`panel.filters.${filterGroup.title}.${filter}`)}
                    </FilterName>
                    <FilterCheck>
                      <Icon name='checkMark' width={16} height={16} colorFill={selectColor('primary')} />
                    </FilterCheck>
                  </FilterRow>
                ))}
              </FilterNameGroup>
            </FilterGroup>
          ))}
        </FiltersGroupsWrapper>
      </ContentMargin>
      {!searchBoxInputIsFocused && (
        <ApplyFiltersButtonWrapper>
          <ApplyFiltersButton
            onClick={handleApplyFilters}
            disabled={JSON.stringify(selectedFilters) === JSON.stringify(appliedFilters)}
            data-panel='filters_showResults'
          >
            {t('panel.filters.btn.apply')}
          </ApplyFiltersButton>
        </ApplyFiltersButtonWrapper>
      )}
    </Wrapper>
  );
}

export { FiltersModal };
